import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, ClipboardList, ExternalLink, Download } from "lucide-react";
import { useHubClinicalTrials } from "@/hooks/useHubResearch";

interface ClinicalTrialsTabProps {
  accountId: string;
  companyName: string;
  hubAccountName: string | null;
}

function statusColor(status: string | null) {
  switch (status?.toLowerCase()) {
    case "recruiting": return "bg-green-100 text-green-800 border-green-200";
    case "active, not recruiting": return "bg-blue-100 text-blue-800 border-blue-200";
    case "completed": return "bg-gray-100 text-gray-700 border-gray-200";
    case "terminated": case "withdrawn": return "bg-red-100 text-red-700 border-red-200";
    case "not yet recruiting": return "bg-yellow-100 text-yellow-800 border-yellow-200";
    default: return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

function phaseColor(phase: string | null) {
  if (!phase) return "bg-gray-100 text-gray-600 border-gray-200";
  if (phase.includes("3")) return "bg-green-100 text-green-800 border-green-200";
  if (phase.includes("2")) return "bg-purple-100 text-purple-800 border-purple-200";
  if (phase.includes("1")) return "bg-blue-100 text-blue-800 border-blue-200";
  if (phase.includes("4")) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  return "bg-gray-100 text-gray-600 border-gray-200";
}

export function ClinicalTrialsTab({ accountId, companyName, hubAccountName }: ClinicalTrialsTabProps) {
  const { data: trials, isLoading } = useHubClinicalTrials(companyName, hubAccountName);

  // Compute summary stats
  const stats = useMemo(() => {
    if (!trials) return { total: 0, phase1: 0, phase2: 0, phase3: 0, recruiting: 0, latestStart: null as string | null };
    const phase1 = trials.filter(t => t.phase?.includes("1") && !t.phase?.includes("2")).length;
    const phase2 = trials.filter(t => t.phase?.includes("2") && !t.phase?.includes("3")).length;
    const phase3 = trials.filter(t => t.phase?.includes("3")).length;
    const recruiting = trials.filter(t => t.overall_status?.toLowerCase() === "recruiting").length;
    const dates = trials.map(t => t.start_date).filter(Boolean).sort().reverse();
    return {
      total: trials.length,
      phase1,
      phase2,
      phase3,
      recruiting,
      latestStart: dates[0] || null,
    };
  }, [trials]);

  // Timeline data — group by year and status
  const timelineData = useMemo(() => {
    if (!trials) return [];
    const yearMap: Record<string, Record<string, number>> = {};
    trials.forEach(t => {
      const year = t.start_date?.substring(0, 4) || "Unknown";
      if (!yearMap[year]) yearMap[year] = {};
      const status = t.overall_status || "Other";
      yearMap[year][status] = (yearMap[year][status] || 0) + 1;
    });
    return Object.entries(yearMap).sort(([a], [b]) => a.localeCompare(b));
  }, [trials]);

  if (isLoading) return <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (!trials?.length) {
    return (
      <div className="p-8 text-center">
        <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No Clinical Trials Found</h3>
        <p className="text-muted-foreground text-sm">No clinical trials found for {companyName} in the research database</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Overview Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Clinical Trials Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div>
            <p className="text-sm text-primary font-medium">Total Trials</p>
            <p className="text-3xl font-bold">{stats.total}</p>
          </div>
          <div>
            <p className="text-sm text-blue-600 font-medium">Phase 1</p>
            <p className="text-3xl font-bold">{stats.phase1}</p>
          </div>
          <div>
            <p className="text-sm text-purple-600 font-medium">Phase 2</p>
            <p className="text-3xl font-bold">{stats.phase2}</p>
          </div>
          <div>
            <p className="text-sm text-green-600 font-medium">Phase 3</p>
            <p className="text-3xl font-bold">{stats.phase3}</p>
          </div>
          <div>
            <p className="text-sm text-emerald-600 font-medium">Recruiting</p>
            <p className="text-3xl font-bold">{stats.recruiting}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground font-medium">Latest Trial Start</p>
            <p className="text-lg font-bold">{stats.latestStart ? new Date(stats.latestStart).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</p>
          </div>
        </div>
      </div>

      {/* Trial Timeline */}
      {timelineData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold mb-3">Trial Timeline</h4>
            <div className="flex items-end gap-1 h-24">
              {timelineData.map(([year, statuses]) => {
                const total = Object.values(statuses).reduce((a, b) => a + b, 0);
                const maxHeight = Math.max(...timelineData.map(([, s]) => Object.values(s).reduce((a, b) => a + b, 0)));
                const height = maxHeight > 0 ? (total / maxHeight) * 100 : 0;
                return (
                  <div key={year} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-primary/20 rounded-t-sm min-h-[4px] transition-all"
                      style={{ height: `${height}%` }}
                      title={`${year}: ${total} trials`}
                    />
                    <span className="text-[10px] text-muted-foreground">{year}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Recruiting</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Active</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" />Completed</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Terminated/Withdrawn</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" />Other</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trials Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold">Clinical Trials ({trials.length})</h4>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">NCT ID</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>Enrollment</TableHead>
              <TableHead>Modality</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trials.map((trial) => (
              <TableRow key={trial.id}>
                <TableCell>
                  <a
                    href={`https://clinicaltrials.gov/study/${trial.nct_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1 text-xs font-mono"
                  >
                    {trial.nct_id}<ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
                <TableCell>
                  <span className="text-sm line-clamp-2">{trial.title}</span>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-xs ${phaseColor(trial.phase)}`}>
                    {trial.phase || "N/A"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-xs ${statusColor(trial.overall_status)}`}>
                    {trial.overall_status || "Unknown"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {trial.start_date ? new Date(trial.start_date).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell className="text-sm">{trial.enrollment?.toLocaleString() || "—"}</TableCell>
                <TableCell>
                  {trial.modality ? (
                    <Badge variant="secondary" className="text-xs">{trial.modality}</Badge>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
